import { parseUTCDate } from '@/lib/utils'
import type { ChatContext, ChatListItem, ChatPage } from '@/features/storefront-chat/types'

const STORE_ORIGIN = 'https://shopusars.com'

/** A store path (``/products/x``) or full URL, as a link that opens our store. */
export function storeUrl(pathOrUrl: string) {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl
  return `${STORE_ORIGIN}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`
}

/** What the page is, in the words a person would use: the product, the search, "Cart". */
export function pageLabel(page: ChatPage) {
  switch (page.page_type) {
    case 'search':
      return page.search_query ? `Search “${page.search_query}”` : 'Search'
    case 'cart':
      return 'Cart'
    case 'home':
      return 'Home page'
    case 'account':
      return 'Account'
    default:
      return (page.title && decodeEntities(page.title)) || page.product_handle || page.collection_handle || page.path
  }
}

/** The store reports page titles HTML-escaped ("Refrigerators &amp; Freezers"); show the text. */
function decodeEntities(text: string) {
  if (!text.includes('&') || typeof document === 'undefined') return text
  const textarea = document.createElement('textarea')
  textarea.innerHTML = text
  return textarea.value
}

/** The shopper counts as on the page while the widget reported it in the last few minutes. */
const LIVE_WINDOW_MS = 5 * 60_000

export function isOnPageNow(seenAt: string | null | undefined, now = Date.now()) {
  return !!seenAt && now - parseUTCDate(seenAt).getTime() < LIVE_WINDOW_MS
}

/** A YYYY-MM-DD date written out in full, e.g. "Sunday, November 15th" (with the year when it isn't
 * this year); without the weekday when ``weekday`` is false. */
export function needByLabel(dateStr: string, { weekday = true, today = new Date() } = {}) {
  const [y = 0, m = 1, d = 1] = dateStr.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  const month = date.toLocaleDateString('en-US', { month: 'long' })
  const teen = d % 100 >= 11 && d % 100 <= 13
  const suffix = teen ? 'th' : (['th', 'st', 'nd', 'rd'][d % 10] ?? 'th')
  const day = `${month} ${d}${suffix}`
  const label = weekday ? `${date.toLocaleDateString('en-US', { weekday: 'long' })}, ${day}` : day
  return y === today.getFullYear() ? label : `${label}, ${y}`
}

export function shopperDisplayName(chat: Pick<ChatListItem, 'shopper_name' | 'shopper_email' | 'customer'>) {
  return chat.shopper_name || chat.customer?.name || chat.shopper_email || 'Guest'
}

/** Whether the chat has anything for the staff context panel: a product with our pricing, a cart, or customer facts. */
export function hasStaffContext(context: ChatContext | undefined) {
  if (!context) return false
  const c = context.customer
  return Boolean(
    context.viewing_handle ||
      context.cart ||
      c.orders_count != null ||
      c.company ||
      c.location ||
      c.phone ||
      c.shipping_zip ||
      c.need_by ||
      c.tax_exempt,
  )
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'

/** A chat photo or file's signed link, which opens it without signing in (USA-1323). */
export function attachmentUrl(attachment: { url: string }) {
  return `${API_BASE_URL}${attachment.url}`
}

/** "Read 2:16 PM" under a reply the shopper has seen (USA-1463); the date too when they read it
 * on a later day than it was sent. */
export function readLabel(readAt: string, sentAt: string) {
  const read = parseUTCDate(readAt)
  const sameDay = read.toDateString() === parseUTCDate(sentAt).toDateString()
  return `Read ${read.toLocaleString(undefined, {
    ...(sameDay ? {} : { month: 'short', day: 'numeric' }),
    ...(read.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  })}`
}

/** A message as one line of plain text for the chat list: AI answers are Markdown, and the list
 * shows the words, not ``**`` or ``[link](…)``. */
export function plainPreview(text: string) {
  return text
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/(^|\W)[*_](\S(?:.*?\S)?)[*_](?=\W|$)/g, '$1$2')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/^\s{0,3}(#{1,6}\s+|>\s?|[-*+]\s+|\d+\.\s+)/gm, '')
    .replace(/\s+/g, ' ')
    .trim()
}
